// @flow
import { resolve, reject } from 'redux-optimist-promise';
import { get, find } from 'lodash';
import { getPriceDetailFragment } from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import { getDefaultProposal } from 'Utils/proposal/proposalUtils';
import { graphqlify } from 'Utils/graphql/index';

import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { PROPOSAL_GET_ADDITIONAL_ACCOMMODATIONS } from 'Utils/request/requestDictionary';
import createAction from 'Store/utils/createAction';
import {
  getAdditionalAccommodations,
  getAdditionalAccommodationsFilters,
  getProposals,
  getSelectedProposalId,
  getCurrentResort,
} from 'Store/booking/booking.selector';

const CLEAR_ADDITIONAL_ACCOMMODATIONS = 'CLEAR_ADDITIONAL_ACCOMMODATIONS';
const FETCH_ADDITIONAL_ACCOMMODATIONS = 'FETCH_ADDITIONAL_ACCOMMODATIONS';
const CREATE_ADDITIONAL_ACCOMMODATIONS_FILTERS = 'CREATE_ADDITIONAL_ACCOMMODATIONS_FILTERS';
const SET_FILTERED_ADDITIONAL_ACCOMMODATIONS = 'SET_FILTERED_ADDITIONAL_ACCOMMODATIONS';
const TOGGLE_ACCOMMODATIONS_FILTER = 'TOGGLE_ACCOMMODATIONS_FILTER';
const UNSELECT_ADDITIONAL_ACCOMMODATIONS_FILTER_CATEGORY =
  'UNSELECT_ADDITIONAL_ACCOMMODATIONS_FILTER_CATEGORY';
const UPDATE_FILTERED_ACCOMMODATIONS_RESULTS = 'UPDATE_FILTERED_ACCOMMODATIONS_RESULTS';

function _getSelectedProposal(state: ReduxState): ?Proposal {
  if (!state) return null;
  const proposals = getProposals(state) || [];
  const resort = getCurrentResort(state);
  const selectedProposal: ?Proposal =
    find(proposals, { id: getSelectedProposalId(state) }) || getDefaultProposal(proposals, resort);
  return selectedProposal;
}

type AdditionalAccommodationsFiltersProps = {
  proposal: Proposal,
  resort: Resort,
  getState: GetReduxState,
  i18n: CMSContent,
  shouldPreselectFilters: boolean,
};
function _createAdditionalAccommodationsFilters({
  proposal,
  resort,
  getState,
  i18n,
  shouldPreselectFilters,
}: AdditionalAccommodationsFiltersProps) {
  const state = getState();

  return createAction(CREATE_ADDITIONAL_ACCOMMODATIONS_FILTERS, {
    additionalAccommodations: getAdditionalAccommodations(state),
    selectedProposal: proposal,
    i18n,
    shouldPreselectFilters,
    resort,
  });
}

function _toggleFilter(filter: Filter, getState: GetReduxState) {
  return createAction(TOGGLE_ACCOMMODATIONS_FILTER, {
    additionalAccommodations: getAdditionalAccommodations(getState()),
    selectedProposal: _getSelectedProposal(getState()),
    filter,
  });
}

function _unselectFiltersCategory(category: string, getState: GetReduxState) {
  return createAction(UNSELECT_ADDITIONAL_ACCOMMODATIONS_FILTER_CATEGORY, {
    additionalAccommodations: getAdditionalAccommodations(getState()),
    selectedProposal: _getSelectedProposal(getState()),
    category,
  });
}

function _updateFilterAccommodations(getState: GetReduxState) {
  return createAction(UPDATE_FILTERED_ACCOMMODATIONS_RESULTS, {
    additionalAccommodations: getAdditionalAccommodations(getState()),
    selectedProposal: _getSelectedProposal(getState()),
    filters: getAdditionalAccommodationsFilters(getState()),
  });
}

type additionalAccommodationsQueryArgs = {
  proposalId: string,
  appContext: AppContext,
  duration: number,
  selectedRate?: string,
  roomsArrangement: Array<*>,
};
function _runAdditionalAccommodationsQuery({
  proposalId,
  appContext,
  duration,
  selectedRate = '',
  roomsArrangement = [],
}: additionalAccommodationsQueryArgs): Promise<*> {
  const query = `
    {
      additionalAccommodations(proposalId: "${proposalId}", roomsArrangement:${graphqlify(
    roomsArrangement,
  )}) {
        id
        totalDifferentialPrice(selectedRate: "${selectedRate}") {
          ...priceDetails
        }
        differentialPriceByNight(nightsNumber: ${duration}, selectedRate: "${selectedRate}") {
          formattedValue(precision: 0)
          value
        }
        remainingStock
        accommodations {
          id
          categories { id label }
          category
          categoryId
          comfortType
          connectingRoom
          description
          label
          maxCapacity
          roomType
          media {
            photos {
              url
            }
          }
          minCapacity
          occupation
          quantity
          occupancies {
            baby_count
          }
          outsideFacilities {
            id
            label
          }
          bathroomEquipments {
            id
            label
          }
          equipments {
            id
            label
          }
          services { id label }
          strengths { id label }
          surface
          view { id label }
          reducedMobilityAccess
          attendeesDistribution {
            id
            quantity
            attendees {
              id
              type
              birthdate
              customerId
            }
          }
        }
      }
      config {
        filters {
          configuration {
            categoryTitle
            simpleRoom
            simpleRooms
            separatedRooms
            separatedRooms3
          }
          comfort {
            categoryTitle
          }
          extraFeatures {
            categoryTitle
          }
          reducedMobility
        }
      }
    }
    ${getPriceDetailFragment(get(appContext, 'locale'))}
  `;

  return graphQLClient.runNoCacheQuery(query, appContext, {
    requestName: PROPOSAL_GET_ADDITIONAL_ACCOMMODATIONS,
  });
}

function clearAdditionalAccommodations() {
  return createAction(CLEAR_ADDITIONAL_ACCOMMODATIONS);
}

export type FetchAdditionalAccommodationsArgs = {
  proposal: Proposal,
  resort: Resort,
  appContext: AppContext,
  roomsArrangement?: Array<{
    attendees: Array<{
      id: string,
    }>,
    categories: Array<string>,
  }>,
  shouldPreselectFilters?: boolean,
};
function fetchAdditionalAccommodations({
  proposal,
  resort,
  appContext,
  roomsArrangement = [],
  shouldPreselectFilters = true,
}: FetchAdditionalAccommodationsArgs) {
  const {
    id,
    duration,
    price: { selectedRate },
  } = proposal;
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    dispatch(createAction(FETCH_ADDITIONAL_ACCOMMODATIONS));

    return _runAdditionalAccommodationsQuery({
      proposalId: id,
      appContext,
      duration,
      selectedRate,
      roomsArrangement,
    })
      .then(({ additionalAccommodations, config: i18n }) => {
        dispatch(
          createAction(resolve(FETCH_ADDITIONAL_ACCOMMODATIONS), { additionalAccommodations }),
        );
        dispatch(
          _createAdditionalAccommodationsFilters({
            proposal,
            resort,
            getState,
            i18n,
            shouldPreselectFilters,
          }),
        );
        dispatch(_updateFilterAccommodations(getState));
      })
      .catch(error => {
        dispatch(createAction(reject(FETCH_ADDITIONAL_ACCOMMODATIONS), error));
      });
  };
}

function toggleAdditionalAccommodationFilter(filter: Filter) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    dispatch(_toggleFilter(filter, getState));
    dispatch(_updateFilterAccommodations(getState));
  };
}

function unselectAdditionalAccommodationsFilterCategory(category: string) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    dispatch(_unselectFiltersCategory(category, getState));
    dispatch(_updateFilterAccommodations(getState));
  };
}

export {
  CLEAR_ADDITIONAL_ACCOMMODATIONS,
  FETCH_ADDITIONAL_ACCOMMODATIONS,
  SET_FILTERED_ADDITIONAL_ACCOMMODATIONS,
  TOGGLE_ACCOMMODATIONS_FILTER,
  UNSELECT_ADDITIONAL_ACCOMMODATIONS_FILTER_CATEGORY,
  CREATE_ADDITIONAL_ACCOMMODATIONS_FILTERS,
  UPDATE_FILTERED_ACCOMMODATIONS_RESULTS,
  clearAdditionalAccommodations,
  fetchAdditionalAccommodations,
  toggleAdditionalAccommodationFilter,
  unselectAdditionalAccommodationsFilterCategory,
};
